import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import {
  Layers,
  List,
  Map as MapIcon,
  ExternalLink,
  ChevronRight,
  CloudRain,
  Activity,
  Flame,
  Radio,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  History,
  Info,
} from 'lucide-react';
import { NormalizedAlert } from '../../types/alert';
import { SeverityBadge } from '../common/SeverityBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { Link } from 'react-router-dom';

interface AlertMapProps {
  alerts: NormalizedAlert[];
  center?: [number, number];
  zoom?: number;
  height?: string;
  className?: string;
  showHistoryToggle?: boolean;
}

interface RadarFrame {
  time: number;
  path: string;
}

// Advanced Pulsing Radar DivIcon with distinctive storm & convective cell styling
const createSmartRadarIcon = (alert: NormalizedAlert) => {
  const isQuake = alert.disruptionType === 'Earthquake';
  const isExpired = alert.isExpired;
  const severity = alert.severity;
  const isSevereConvective =
    alert.disruptionType === 'Severe' ||
    alert.disruptionType === 'Tropical' ||
    alert.event.toLowerCase().includes('tornado') ||
    alert.event.toLowerCase().includes('severe thunderstorm');

  let color = '#3b82f6'; // blue
  let pulseColor = 'rgba(59, 130, 246, 0.4)';
  let isSpecialPulsing = false;

  if (isExpired) {
    color = '#64748b'; // slate grey
    pulseColor = 'rgba(100, 116, 139, 0.2)';
  } else if (isQuake) {
    color = '#8b5cf6'; // purple for seismic
    pulseColor = 'rgba(139, 92, 246, 0.55)';
    isSpecialPulsing = true;
  } else if (severity === 'Extreme' || alert.event.toLowerCase().includes('tornado')) {
    color = '#ef4444'; // red
    pulseColor = 'rgba(239, 68, 68, 0.7)';
    isSpecialPulsing = true;
  } else if (severity === 'Severe') {
    color = '#f97316'; // orange
    pulseColor = 'rgba(249, 115, 22, 0.55)';
    isSpecialPulsing = true;
  } else if (severity === 'Moderate') {
    color = '#f59e0b'; // amber
    pulseColor = 'rgba(245, 158, 11, 0.4)';
  }

  const badgeContent = isExpired
    ? 'CLR'
    : isQuake && alert.magnitude
    ? `M${alert.magnitude.toFixed(1)}`
    : isSevereConvective
    ? '⚡'
    : '';

  const beaconClass = !isExpired && isSpecialPulsing ? 'storm-cell-beacon' : '';

  return L.divIcon({
    className: 'radar-marker-wrap',
    html: `
      <div class="${beaconClass}" style="position: relative; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;">
        ${
          !isExpired
            ? `<div style="position: absolute; width: 100%; height: 100%; border-radius: 50%; background-color: ${pulseColor}; animation: radarRipple 1.8s ease-out infinite;"></div>`
            : ''
        }
        <div style="position: relative; min-width: 16px; height: 16px; padding: 0 4px; border-radius: 8px; background-color: ${color}; border: 2px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; font-family: monospace; font-size: 9px; font-weight: 800; color: white;">
          ${badgeContent}
        </div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -16],
  });
};

export const AlertMap: React.FC<AlertMapProps> = ({
  alerts,
  center = [38.5, -96.5],
  zoom = 4,
  height = '540px',
  className = '',
  showHistoryToggle = true,
}) => {
  const [activeTab, setActiveTab] = useState<'map' | 'list'>('map');
  const [showRadarOverlay, setShowRadarOverlay] = useState<boolean>(true);
  const [radarHost, setRadarHost] = useState<string>('https://tilecache.rainviewer.com');
  const [radarFrames, setRadarFrames] = useState<RadarFrame[]>([]);
  const [currentFrameIdx, setCurrentFrameIdx] = useState<number>(0);
  const [isPlayingRadar, setIsPlayingRadar] = useState<boolean>(false);
  const [includeExpired, setIncludeExpired] = useState<boolean>(false);
  const [layerFilter, setLayerFilter] = useState<'ALL' | 'WEATHER' | 'QUAKE'>('ALL');

  const animationTimerRef = useRef<any>(null);

  // Fetch RainViewer past frames array for Doppler radar animation
  useEffect(() => {
    const fetchRadarMetadata = async () => {
      try {
        const res = await fetch('https://api.rainviewer.com/public/weather-maps.json');
        if (res.ok) {
          const data = await res.json();
          if (data && data.host && data.radar && Array.isArray(data.radar.past) && data.radar.past.length > 0) {
            setRadarHost(data.host);
            setRadarFrames(data.radar.past);
            // Default to latest frame
            setCurrentFrameIdx(data.radar.past.length - 1);
          }
        }
      } catch (e) {
        console.warn('RainViewer live radar overlay unavailable, fallback to base cartography:', e);
      }
    };
    fetchRadarMetadata();
  }, []);

  // Radar playback ticker loop
  useEffect(() => {
    if (!isPlayingRadar || radarFrames.length === 0) {
      if (animationTimerRef.current) clearInterval(animationTimerRef.current);
      return;
    }

    animationTimerRef.current = setInterval(() => {
      setCurrentFrameIdx((prev) => (prev + 1) % radarFrames.length);
    }, 750);

    return () => {
      if (animationTimerRef.current) clearInterval(animationTimerRef.current);
    };
  }, [isPlayingRadar, radarFrames.length]);

  // Current radar tile URL
  const currentTileUrl =
    radarFrames.length > 0 && currentFrameIdx < radarFrames.length
      ? `${radarHost}${radarFrames[currentFrameIdx].path}/256/{z}/{x}/{y}/2/1_1.png`
      : null;

  const currentFrameTimeLabel = () => {
    if (radarFrames.length === 0 || currentFrameIdx >= radarFrames.length) return 'LIVE';
    const frame = radarFrames[currentFrameIdx];
    const date = new Date(frame.time * 1000);
    const isLatest = currentFrameIdx === radarFrames.length - 1;
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return isLatest ? `${timeStr} (LIVE)` : `${timeStr} (Loop)`;
  };

  // Filter alerts by centroid/geometry, expired toggle, and layer type
  const mappableAlerts = alerts
    .filter((a) => Boolean(a.centroid || a.geometry))
    .filter((a) => (includeExpired ? true : !a.isExpired))
    .filter((a) => {
      if (layerFilter === 'WEATHER') return a.disruptionType !== 'Earthquake';
      if (layerFilter === 'QUAKE') return a.disruptionType === 'Earthquake';
      return true;
    });

  const activeCount = mappableAlerts.filter((a) => !a.isExpired).length;
  const expiredCount = mappableAlerts.filter((a) => a.isExpired).length;

  return (
    <div className={`bg-navy-950 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl ${className}`}>
      {/* Tactical Top Telemetry & Control Bar */}
      <div className="px-5 py-3 bg-navy-950 text-white flex flex-wrap items-center justify-between gap-3 border-b border-slate-800">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-cyan-500"></span>
          </div>
          <span className="text-xs font-mono font-bold uppercase tracking-wider text-white">
            Live Geospatial Radar
          </span>
          <span className="text-xs font-mono text-cyan-400 bg-cyan-950/80 px-2 py-0.5 rounded border border-cyan-800/80">
            {activeCount} ACTIVE {includeExpired && `• ${expiredCount} CLEARED`}
          </span>

          {/* Radar Overlay Toggle */}
          <button
            onClick={() => setShowRadarOverlay(!showRadarOverlay)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono font-semibold transition-all border ${
              showRadarOverlay
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40 shadow-xs'
                : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
            }`}
            title="Toggle real-time RainViewer precipitation Doppler radar layer"
          >
            <CloudRain className="w-3.5 h-3.5" />
            <span>DOPPLER: {showRadarOverlay ? 'ON' : 'OFF'}</span>
          </button>

          {/* Expired / History Zones Toggle */}
          {showHistoryToggle && (
            <button
              onClick={() => setIncludeExpired(!includeExpired)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-mono font-semibold transition-all border ${
                includeExpired
                  ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-xs'
                  : 'bg-slate-800/70 text-slate-400 border-slate-700 hover:text-white'
              }`}
              title="Show storm warnings that cleared in the last 24 hours"
            >
              <History className="w-3.5 h-3.5" />
              <span>PAST 24H CLEARED: {includeExpired ? 'VISIBLE' : 'HIDDEN'}</span>
            </button>
          )}
        </div>

        {/* View Switcher Segmented Control */}
        <div className="inline-flex rounded-xl bg-slate-900 p-1 border border-slate-800 text-xs font-medium">
          <button
            onClick={() => setActiveTab('map')}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg transition-all ${
              activeTab === 'map'
                ? 'bg-blue-600 text-white shadow-2xs font-bold'
                : 'text-slate-400 hover:text-white'
            }`}
            aria-pressed={activeTab === 'map'}
          >
            <MapIcon className="w-3.5 h-3.5" />
            <span>Cartography</span>
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg transition-all ${
              activeTab === 'list'
                ? 'bg-blue-600 text-white shadow-2xs font-bold'
                : 'text-slate-400 hover:text-white'
            }`}
            aria-pressed={activeTab === 'list'}
          >
            <List className="w-3.5 h-3.5" />
            <span>Accessible List</span>
          </button>
        </div>
      </div>

      {/* Layer Filter & Radar Animation HUD Sub-Strip */}
      <div className="px-5 py-2.5 bg-slate-950 border-b border-slate-800 flex flex-wrap items-center justify-between text-xs text-slate-300 gap-3">
        {/* Category Filters */}
        <div className="flex items-center gap-2 font-mono text-[11px]">
          <span className="text-slate-400">LAYER:</span>
          <button
            onClick={() => setLayerFilter('ALL')}
            className={`px-2 py-0.5 rounded transition-colors ${
              layerFilter === 'ALL' ? 'bg-slate-800 text-white font-bold' : 'text-slate-400 hover:text-white'
            }`}
          >
            All Hazards
          </button>
          <button
            onClick={() => setLayerFilter('WEATHER')}
            className={`px-2 py-0.5 rounded transition-colors ${
              layerFilter === 'WEATHER'
                ? 'bg-blue-900/60 text-blue-300 font-bold border border-blue-700/60'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Weather (NWS)
          </button>
          <button
            onClick={() => setLayerFilter('QUAKE')}
            className={`px-2 py-0.5 rounded transition-colors ${
              layerFilter === 'QUAKE'
                ? 'bg-purple-900/60 text-purple-300 font-bold border border-purple-700/60'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Earthquakes (USGS)
          </button>
        </div>

        {/* RainViewer Doppler Radar Animation Loop Player */}
        {showRadarOverlay && radarFrames.length > 0 && (
          <div className="flex items-center gap-2 bg-slate-900/90 px-3 py-1 rounded-xl border border-slate-800 text-xs font-mono">
            <button
              onClick={() => setIsPlayingRadar(!isPlayingRadar)}
              className="p-1 rounded hover:bg-slate-800 text-cyan-400 transition-colors"
              title={isPlayingRadar ? 'Pause radar animation' : 'Play Doppler radar loop'}
            >
              {isPlayingRadar ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>

            <button
              onClick={() =>
                setCurrentFrameIdx((prev) => (prev > 0 ? prev - 1 : radarFrames.length - 1))
              }
              className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
              title="Previous frame"
            >
              <SkipBack className="w-3 h-3" />
            </button>

            <input
              type="range"
              min={0}
              max={radarFrames.length - 1}
              value={currentFrameIdx}
              onChange={(e) => {
                setIsPlayingRadar(false);
                setCurrentFrameIdx(Number(e.target.value));
              }}
              className="w-20 sm:w-28 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-cyan-400"
            />

            <button
              onClick={() => setCurrentFrameIdx((prev) => (prev + 1) % radarFrames.length)}
              className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
              title="Next frame"
            >
              <SkipForward className="w-3 h-3" />
            </button>

            <span className="text-[10px] text-cyan-300 font-bold px-1.5 py-0.5 bg-slate-800 rounded">
              {currentFrameTimeLabel()}
            </span>
          </div>
        )}
      </div>

      {/* Map Container View */}
      {activeTab === 'map' ? (
        <div style={{ height }} className="w-full relative z-10 bg-slate-950">
          <MapContainer
            center={center}
            zoom={zoom}
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            {/* Base OpenStreetMap Cartography */}
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={18}
            />

            {/* Live RainViewer Precipitation Radar Overlay Frame */}
            {showRadarOverlay && currentTileUrl && (
              <TileLayer
                key={currentTileUrl}
                attribution='Radar &copy; <a href="https://www.rainviewer.com" target="_blank" rel="noopener noreferrer">RainViewer</a>'
                url={currentTileUrl}
                opacity={0.78}
                zIndex={100}
                maxZoom={18}
              />
            )}

            {/* Official NWS Warning Polygons */}
            {mappableAlerts.map((alert) => {
              if (
                !alert.geometry ||
                (alert.geometry.type !== 'Polygon' && alert.geometry.type !== 'MultiPolygon')
              )
                return null;

              let color = '#3b82f6';
              let dashArray: string | undefined = undefined;
              let fillOpacity = 0.25;

              if (alert.isExpired) {
                color = '#64748b'; // muted slate for expired
                dashArray = '5, 5';
                fillOpacity = 0.08;
              } else if (alert.severity === 'Extreme') {
                color = '#ef4444';
                fillOpacity = 0.35;
              } else if (alert.severity === 'Severe') {
                color = '#f97316';
                fillOpacity = 0.3;
              } else if (alert.severity === 'Moderate') {
                color = '#f59e0b';
                fillOpacity = 0.25;
              }

              return (
                <GeoJSON
                  key={`poly_${alert.id}_${alert.isExpired ? 'exp' : 'act'}`}
                  data={alert.geometry as any}
                  style={{
                    color,
                    weight: alert.isExpired ? 1.5 : 2.5,
                    opacity: alert.isExpired ? 0.6 : 0.95,
                    fillColor: color,
                    fillOpacity,
                    dashArray,
                  }}
                />
              );
            })}

            {/* Plotted Disruption Markers */}
            {mappableAlerts.map((alert) => {
              if (!alert.centroid) return null;
              return (
                <Marker
                  key={alert.id}
                  position={alert.centroid}
                  icon={createSmartRadarIcon(alert)}
                >
                  <Popup className="custom-alert-popup">
                    <div className="p-1 space-y-2 max-w-xs text-xs font-sans">
                      <div className="flex items-center justify-between gap-2">
                        {alert.isExpired ? (
                          <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-200 text-slate-700">
                            CLEARED / EXPIRED
                          </span>
                        ) : (
                          <SeverityBadge severity={alert.severity} size="sm" />
                        )}
                        <span className="text-[10px] font-mono text-slate-500">
                          {formatRelativeTime(alert.effective)}
                        </span>
                      </div>
                      <h4 className="font-bold text-navy-950 text-sm leading-tight">
                        {alert.event}
                      </h4>
                      <p className="text-slate-600 line-clamp-2 leading-relaxed">
                        {alert.areaDesc}
                      </p>
                      <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                        {alert.disruptionType === 'Earthquake' ? (
                          <a
                            href={alert.sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-purple-600 hover:text-purple-800 flex items-center gap-1"
                          >
                            <span>USGS Event Page</span>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : (
                          <Link
                            to={`/alert/${encodeURIComponent(alert.id)}`}
                            className="font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            <span>{alert.isExpired ? 'Audit Record' : 'Full Bulletin'}</span>
                            <ChevronRight className="w-3.5 h-3.5" />
                          </Link>
                        )}
                        <span className="text-[10px] font-mono text-slate-400">
                          {alert.sourceAgency}
                        </span>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>

          {/* Floating Doppler Radar dBZ Intensity Legend Bar */}
          {showRadarOverlay && (
            <div className="absolute bottom-4 left-4 z-[400] bg-slate-950/90 backdrop-blur-md px-3.5 py-2.5 rounded-xl border border-slate-700/80 shadow-2xl pointer-events-auto">
              <div className="flex items-center justify-between gap-3 mb-1.5">
                <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
                  Doppler Rain & Storm Intensity (dBZ)
                </span>
                <span className="text-[9px] font-mono text-slate-400">RainViewer NEXRAD</span>
              </div>
              
              <div className="flex items-center gap-1">
                <span className="text-[9px] font-mono text-slate-400 mr-1">Light</span>
                {/* Visual dBZ gradient scale bar */}
                <div
                  className="h-2 w-36 sm:w-48 rounded-full"
                  style={{
                    background: 'linear-gradient(to right, #00ecec, #01a0f6, #0000f6, #00e000, #009000, #ffff00, #e7c000, #ff9000, #ff0000, #d60000, #c00000, #ff00f0, #9600b4)',
                  }}
                  title="Rainfall reflectivity from light drizzle (10 dBZ) to severe storm & hail (65+ dBZ)"
                />
                <span className="text-[9px] font-mono text-red-400 font-bold ml-1">Severe / Hail</span>
              </div>

              <div className="flex items-center justify-between text-[8.5px] font-mono text-slate-400 mt-1">
                <span>Drizzle</span>
                <span>Rain</span>
                <span>Heavy</span>
                <span className="text-amber-400">Storm</span>
                <span className="text-fuchsia-400 font-bold">Hail / Extreme</span>
              </div>
            </div>
          )}

          {/* Live Doppler Feed Indicator Stamp */}
          <div className="absolute top-4 right-4 z-[400] bg-slate-950/85 backdrop-blur-md px-2.5 py-1 rounded-lg border border-slate-800 text-[10px] font-mono text-slate-300 shadow-lg pointer-events-none flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
            <span>Real-Time Weather Stream</span>
          </div>
        </div>
      ) : (
        /* Accessible List Alternative for Screen Readers */
        <div
          style={{ minHeight: height, maxHeight: '600px' }}
          className="overflow-y-auto p-5 divide-y divide-slate-800 bg-navy-950 text-white"
        >
          <div className="text-xs font-mono text-slate-400 mb-3 uppercase tracking-wider flex items-center justify-between">
            <span>Screen-reader accessible tabular list of plotted disruption zones ({mappableAlerts.length}):</span>
            {includeExpired && <span className="text-amber-400">Includes 24h cleared events</span>}
          </div>
          {mappableAlerts.length === 0 ? (
            <p className="text-sm text-slate-400 py-10 text-center">
              No plotted alerts currently match this filter.
            </p>
          ) : (
            mappableAlerts.map((alert) => (
              <div key={alert.id} className="py-3.5 flex items-start justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {alert.isExpired ? (
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-slate-800 text-slate-300 border border-slate-700">
                        CLEARED
                      </span>
                    ) : (
                      <SeverityBadge severity={alert.severity} size="sm" />
                    )}
                    <span className="font-bold text-white text-sm">{alert.event}</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-900 text-slate-400 border border-slate-800">
                      {alert.sourceAgency}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300">
                    <strong>Affected Areas:</strong> {alert.areaDesc}
                  </p>
                  <p className="text-[11px] font-mono text-slate-400">
                    Issuer: {alert.senderName} • Effective: {formatRelativeTime(alert.effective)}
                    {alert.isExpired && ` • Expired: ${formatRelativeTime(alert.expiredAt || alert.expires)}`}
                  </p>
                </div>
                {alert.disruptionType === 'Earthquake' ? (
                  <a
                    href={alert.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-purple-400 hover:underline flex-shrink-0 flex items-center gap-1"
                  >
                    <span>USGS Page</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <Link
                    to={`/alert/${encodeURIComponent(alert.id)}`}
                    className="text-xs font-bold text-cyan-400 hover:underline flex-shrink-0"
                  >
                    View Details &rarr;
                  </Link>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Map Footer HUD */}
      <div className="px-5 py-2.5 bg-slate-950 border-t border-slate-800 text-[11px] font-mono text-slate-400 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-slate-300 font-semibold uppercase">Hazards:</span>
          <span className="inline-flex items-center gap-1.5 text-red-400">
            <span className="w-2 h-2 rounded-full bg-red-500 inline-block animate-pulse" /> Extreme
          </span>
          <span className="inline-flex items-center gap-1.5 text-orange-400">
            <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" /> Severe
          </span>
          <span className="inline-flex items-center gap-1.5 text-amber-400">
            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> Moderate
          </span>
          <span className="inline-flex items-center gap-1.5 text-blue-400">
            <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Minor
          </span>
          <span className="inline-flex items-center gap-1.5 text-purple-400">
            <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" /> Seismic (USGS)
          </span>
          {includeExpired && (
            <span className="inline-flex items-center gap-1.5 text-slate-400">
              <span className="w-2 h-2 rounded-full bg-slate-600 inline-block" /> Cleared (Past 24h)
            </span>
          )}
        </div>
        <div className="text-slate-500">
          OpenStreetMap &amp; RainViewer Doppler Radar API
        </div>
      </div>
    </div>
  );
};
